import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import type { NodeCatalogue, NodeDef } from "../../types/flows";
import { HelpTooltip } from "./NodeHelp";
import { accentOf, input, NodeIcon } from "./ui";

// Drag to the canvas, or click to append to whatever is selected. Both exist
// because dragging is precise and clicking is fast, and building a ten-node
// chain by dragging each one into place is tedious.

export const Palette = ({
	catalogue,
	onAppend,
}: {
	catalogue: NodeCatalogue;
	onAppend: (def: NodeDef) => void;
}) => {
	const [query, setQuery] = useState("");

	const groups = useMemo(() => {
		const needle = query.trim().toLowerCase();
		return catalogue.categories
			.map((category) => ({
				category,
				nodes: catalogue.nodes.filter(
					(node) =>
						node.category === category.id &&
						(needle === "" ||
							node.label.toLowerCase().includes(needle) ||
							node.type.toLowerCase().includes(needle) ||
							node.help.summary.toLowerCase().includes(needle)),
				),
			}))
			.filter((group) => group.nodes.length > 0);
	}, [catalogue, query]);

	return (
		<div className="flex h-full w-64 shrink-0 flex-col border-r border-slate-200 bg-white">
			<div className="border-b border-slate-200 p-3">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
					<input
						type="search"
						placeholder="Search nodes"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						className={`${input} pl-8`}
					/>
				</div>
			</div>

			<div className="flex-1 space-y-4 overflow-y-auto p-3">
				{groups.length === 0 && (
					<p className="text-[12px] text-slate-400">
						Nothing matches “{query}”.
					</p>
				)}
				{groups.map(({ category, nodes }) => (
					<div key={category.id}>
						<p
							className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400"
							title={category.description}
						>
							{category.label}
						</p>
						<div className="space-y-1">
							{nodes.map((node) => {
								const tone = accentOf(category.accent);
								return (
									<div key={node.type} className="group relative">
										{/* Drag lives on the button, not the wrapper, so the entry
										    stays a real control for keyboard and screen readers. */}
										<button
											type="button"
											draggable
											onDragStart={(event) => {
												event.dataTransfer.setData(
													"application/flow-node",
													node.type,
												);
												event.dataTransfer.effectAllowed = "move";
											}}
											onClick={() => onAppend(node)}
											className="flex w-full cursor-grab items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 text-left transition-colors hover:border-slate-200 hover:bg-slate-50"
										>
											<span
												className={`rounded-md p-1 ${tone.bg} ${tone.text}`}
											>
												<NodeIcon name={node.icon} className="h-3.5 w-3.5" />
											</span>
											<span className="min-w-0 flex-1">
												<span className="block truncate text-[12px] font-medium text-slate-700">
													{node.label}
												</span>
											</span>
										</button>
										<HelpTooltip def={node} />
									</div>
								);
							})}
						</div>
					</div>
				))}
			</div>

			<p className="border-t border-slate-200 px-3 py-2 text-[10px] leading-relaxed text-slate-400">
				Drag onto the canvas, or click to append to the selected node.
			</p>
		</div>
	);
};
